/**
 * Utility functions for bidirectional GUID-color mapping
 * Uses first 6 hex digits of GUID as RGB color encoding
 */

/**
 * Extracts the color-coded portion from GUID and interprets as RGB
 * @param guid The GUID string to convert
 * @returns CSS color string in format "rgb(r, g, b)"
 */
export function guidToColor(guid: string): string {
  if (!guid) return 'rgb(128, 128, 128)'; // Default gray for empty GUID
  
  // Remove dashes and convert to lowercase for consistent processing
  const cleanGuid = guid.replace(/-/g, '').toLowerCase();
  
  // Ensure we have at least 6 hex characters
  if (cleanGuid.length < 6) return 'rgb(128, 128, 128)';
  
  // Extract first 6 hex digits as RGB components
  const r = parseInt(cleanGuid.substring(0, 2), 16);
  const g = parseInt(cleanGuid.substring(2, 4), 16);
  const b = parseInt(cleanGuid.substring(4, 6), 16);
  
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Creates a GUID with the specified RGB color encoded in the first 6 hex digits
 * @param rgb RGB color string in format "rgb(r, g, b)" or "rgb(r,g,b)"
 * @returns GUID string with color encoded in first 6 hex digits
 */
export function colorToGuid(rgb: string): string {
  // Parse RGB string "rgb(r, g, b)"
  const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return generateRandomGuid(); // fallback: random GUID

  const r = Number(match[1]);
  const g = Number(match[2]);
  const b = Number(match[3]);
  
  // Convert RGB to hex and pad with zeros
  const hexPart = [r, g, b]
    .map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0"))
    .join("");

  // Generate remaining 26 random hex digits (32 total - 6 for color)
  const rest = generateHex(26);
  
  const guidRaw = hexPart + rest;
  
  // Format as standard GUID: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  return guidRaw.replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
}

/**
 * Converts a GUID to a hex color string
 * @param guid The GUID string to convert
 * @returns CSS color string in format "#rrggbb"
 */
export function guidToHexColor(guid: string): string {
  if (!guid) return '#808080'; // Default gray for empty GUID
  
  // Remove dashes and convert to lowercase for consistent processing
  const cleanGuid = guid.replace(/-/g, '').toLowerCase();
  
  // Ensure we have at least 6 hex characters
  if (cleanGuid.length < 6) return '#808080';
  
  // Extract first 6 hex digits directly
  return `#${cleanGuid.substring(0, 6)}`;
}

/**
 * Creates a hex color string from RGB color and converts it to GUID
 * @param hexColor Hex color string in format "#rrggbb" or "rrggbb"
 * @returns GUID string with color encoded in first 6 hex digits
 */
export function hexColorToGuid(hexColor: string): string {
  // Remove # if present and ensure 6 characters
  const cleanHex = hexColor.replace('#', '').toLowerCase();
  if (cleanHex.length !== 6 || !/^[0-9a-f]{6}$/.test(cleanHex)) {
    return generateRandomGuid(); // fallback for invalid hex
  }
  
  // Convert hex to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  
  return colorToGuid(`rgb(${r}, ${g}, ${b})`);
}

/**
 * Generates random hex digits of specified length
 * @param length Number of hex digits to generate
 * @returns Random hex string
 */
function generateHex(length: number): string {
  return Array.from({ length }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join("");
}

/**
 * Generates a completely random GUID
 * @returns Random GUID string in standard format
 */
export function generateRandomGuid(): string {
  const guidRaw = generateHex(32);
  return guidRaw.replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
}

/**
 * Gets a lighter variant of the GUID color for backgrounds
 * @param guid The GUID string to convert
 * @param lightnessBoost Additional lightness percentage (0-100)
 * @returns CSS color string in RGB format with increased lightness
 */
export function guidToLightColor(guid: string, lightnessBoost: number = 40): string {
  const baseColor = guidToColor(guid);
  const match = baseColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return 'rgb(200, 200, 200)';
  
  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);
  
  // Convert to HSL for easier lightness manipulation
  const hsl = rgbToHsl(r, g, b);
  
  // Increase lightness
  const newLightness = Math.min(85, hsl.l + lightnessBoost);
  const newSaturation = Math.max(20, hsl.s - 20); // Reduce saturation for lighter variant
  
  return hslToRgb(hsl.h, newSaturation, newLightness);
}

/**
 * Gets a darker variant of the GUID color for borders/text
 * @param guid The GUID string to convert
 * @param darknessBoost Additional darkness percentage (0-50)
 * @returns CSS color string in RGB format with decreased lightness
 */
export function guidToDarkColor(guid: string, darknessBoost: number = 25): string {
  const baseColor = guidToColor(guid);
  const match = baseColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (!match) return 'rgb(80, 80, 80)';
  
  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);
  
  // Convert to HSL for easier lightness manipulation
  const hsl = rgbToHsl(r, g, b);
  
  // Decrease lightness
  const newLightness = Math.max(15, hsl.l - darknessBoost);
  const newSaturation = Math.min(90, hsl.s + 10); // Increase saturation for darker variant
  
  return hslToRgb(hsl.h, newSaturation, newLightness);
}

/**
 * Helper function to convert RGB to HSL
 */
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Helper function to convert HSL to RGB
 */
function hslToRgb(h: number, s: number, l: number): string {
  h /= 360;
  s /= 100;
  l /= 100;
  
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
}
